/**
 * AddProperty.jsx — Restaurant Onboarding (Field-Registry Architecture)
 *
 * ═══════════════════════════════════════════════════════════════════════
 * HOW TO ADD A NEW FIELD — READ THIS FIRST
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Adding a new field/parameter to the onboarding flow is now a ONE-PLACE
 * change. Just add an entry to FIELD_REGISTRY below. That's it.
 *
 * Each registry entry controls:
 *   • Which step it appears in (manual and/or google flows)
 *   • What it renders (the form UI)
 *   • How to extract its value from the form state
 *   • How it's saved to the backend (API key + optional transform)
 *   • Validation rules
 *   • Whether it's optional or required
 *
 * Example — adding a "Phone Number" field to manual step 5 (after website):
 *
 *   {
 *     id: 'phone',
 *     label: 'Phone Number',
 *     optional: true,
 *     manualStep: 5.5,         // insert between step 5 and 6 (auto-sorted)
 *     googleReviewField: true, // also show in Google review form
 *     render: ({ form, setForm }) => (
 *       <>
 *         <label className="ap-label">Phone <span className="ap-optional">(optional)</span></label>
 *         <input
 *           className="ap-input"
 *           type="tel"
 *           value={form.phone}
 *           onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
 *           placeholder="+91 98765 43210"
 *         />
 *       </>
 *     ),
 *     getValue: (form) => form.phone,
 *     save: async (value, updateRes) => updateRes('phone', value),
 *     validate: (form) => {
 *       if (form.phone && !/^\+?\d{7,15}$/.test(form.phone))
 *         return 'Enter a valid phone number'
 *     },
 *   }
 *
 * Then add `phone: ''` to EMPTY_FORM. Done — no other changes needed.
 *
 * ═══════════════════════════════════════════════════════════════════════
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link,useNavigate } from 'react-router-dom'
import Locationpicker from './Locationpicker'
import SelectCategory from './SelectCategory'
import SelectCousine  from './SelectCousine'
import SelectMenuType from './SelectMenuType'
import AddThumbnail   from './AddThumbnail'
import AddImages      from './AddImages'
import { host }       from '../script/variables'
import '../style/addproperty.css'
import AddStartDate from './AddStartDate'


// ─── Utilities ─────────────────────────────────────────────────────────────────

const msToTime = (ms) => {
  if (!ms && ms !== 0) return ''
  const h = Math.floor(ms / 3600000).toString().padStart(2, '0')
  const m = Math.floor((ms % 3600000) / 60000).toString().padStart(2, '0')
  return `${h}:${m}`
}

const timeToMs = (t) => {
  if (!t) return null
  const [h, m] = t.split(':').map(Number)
  return h * 3600000 + m * 60000
}

const token = () => localStorage.getItem('partnertoken')

const timeStr = (t) => {
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = ((h % 12) || 12).toString().padStart(2, '0')
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}


// ─── Default Form State ────────────────────────────────────────────────────────
//
// Add new fields here when you add them to the registry.

const EMPTY_FORM = {
  resname:      '',
  category:     [],
  cuisine:      [],
  ambience:     [],
  vibe:         [],
  occasion:     [],
  menuType:     'Veg',
  opensAt:      '',
  closeAt:      '',
  websiteUrl:   '',
  title:        '',
  description:  '',
  location:     { type: 'Point', coordinates: [77.2090, 28.6139] },
  thumbnailImg: null,
  cafeImg:      [],
  startDate:     null
  // ↑ Add new field defaults above this line
}


// ─── Google Places Helpers ─────────────────────────────────────────────────────

async function fetchPlaceDetails(placeId) {
  const res = await fetch(
    `${host}/gmaps/details?placeId=${encodeURIComponent(placeId)}`,
    { headers: { Authorization: token() } }
  )
  if (!res.ok) throw new Error(`Places details failed: ${res.status}`)
  const data = await res.json()
  return data.result
}

async function searchPlaces(query) {
  const res = await fetch(
    `${host}/gmaps/search?query=${encodeURIComponent(query)}`,
    { headers: { Authorization: token() } }
  )
  if (!res.ok) throw new Error(`Places search failed: ${res.status}`)
  const data = await res.json()
  return data.results?.slice(0, 6) || []
}

function mapGoogleTypeToCategory(types = []) {
  if (types.includes('bar'))           return 'Bar'
  if (types.includes('cafe'))          return 'Café'
  if (types.includes('bakery'))        return 'Bakery'
  if (types.includes('meal_delivery')) return 'Cloud Kitchen'
  return 'Restaurant'
}

function parseGoogleHours(opening_hours) {
  const period = opening_hours?.periods?.[0]
  if (!period) return { opensAt: '', closeAt: '' }
  const fmt = (t) => t ? `${t.time.slice(0, 2)}:${t.time.slice(2)}` : ''
  return { opensAt: fmt(period.open), closeAt: fmt(period.close) }
}

/**
 * Called when a Google Place is selected. Maps Place API fields → form state.
 * To map a new Place API field to a new form field, add it here.
 */
function mapPlaceToForm(details, rawPlace) {
  const coords = details?.geometry?.location
    ? [details.geometry.location.lng, details.geometry.location.lat]
    : EMPTY_FORM.location.coordinates

  const { opensAt, closeAt } = parseGoogleHours(details?.opening_hours)

  return {
    resname:     details?.name    || rawPlace?.name || '',
    websiteUrl:  details?.website || '',
    opensAt,
    closeAt,
    description: details?.editorial_summary?.overview || '',
    category:    mapGoogleTypeToCategory(details?.types || rawPlace?.types || []),
    location:    { type: 'Point', coordinates: coords },
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// FIELD REGISTRY
//
// This is the single source of truth for every onboarding field.
// The component reads this array to build steps, render forms, validate,
// and save — so you never have to edit switch statements again.
//
// FIELD SHAPE:
//   id                  (string)   Unique field key — must match EMPTY_FORM key(s)
//   label               (string)   Human-readable label for step nav & summary
//   icon                (string)   Emoji shown in step sidebar
//   stepTitle           (string)   Heading shown at top of step
//   stepSubtitle        (string)   Subheading shown at top of step
//   optional            (boolean)  If true, "(optional)" shown in label
//   manualStep          (number)   Position in manual flow (use decimals to insert between)
//   googleReviewField   (boolean)  If true, rendered in the Google review form (step 1)
//   render              (fn)       ({ form, setForm, resId }) => ReactNode
//   getValue            (fn)       (form) => the value(s) to save
//   save                (fn)       async (form, updateRes) => void  — handles API call(s)
//   validate            (fn)       (form) => string | undefined  — return error msg or nothing
//   summaryRow          (fn|false) (form) => [label, displayValue] pair, or false to skip summary
// ═══════════════════════════════════════════════════════════════════════════════

const FIELD_REGISTRY = [

  // ── 0: Name + Location ─────────────────────────────────────────────────────
  {
    id:           'nameLocation',
    label:        'Location',
    icon:         '📍',
    stepTitle:    'Name & Location',
    stepSubtitle: 'Where are you located?',
    manualStep:   0,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <>
        <label className="ap-label">
          Property Name <span className="ap-required">*</span>
        </label>
        <input
          className="ap-input"
          value={form.resname}
          onChange={(e) => setForm(f => ({ ...f, resname: e.target.value }))}
          placeholder="e.g. The Golden Fork"
          autoFocus
        />
        <div className="ap-field-group">
          <label className="ap-label">Location</label>
          <Locationpicker
            location={form.location}
            setLocation={(v) => setForm(f => ({ ...f, location: v }))}
          />
        </div>
      </>
    ),

    getValue: (form) => ({ name: form.resname, location: form.location }),

    // Name+Location are created via addNameLocation() — special-cased in handleNext.
    // This save fn is a no-op; the main logic handles it.
    save: async () => {},

    validate: (form) => {
      if (!form.resname.trim()) return 'Property name is required'
    },

    summaryRow: (form) => ['Name', form.resname || '—'],
  },


  // ── 1: Category ────────────────────────────────────────────────────────────
  {
    id:           'category',
    label:        'Category',
    icon:         '🍽️',
    stepTitle:    'Category',
    stepSubtitle: 'What kind of place is this?',
    manualStep:   1,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <SelectCategory
        setCategory={(v) => setForm(f => ({ ...f, category: v }))}
        category={form.category}
      />
    ),

    getValue: (form) => form.category,

    save: async (form, updateRes) => {
      await updateRes('category', form.category)
    },

    validate: (form) => {
      if (!form.category || form.category.length === 0)
        return 'Please select a category'
    },

    summaryRow: (form) => ['Category', form.category || '—'],
  },


  // ── 2: Cuisine ─────────────────────────────────────────────────────────────
  {
    id:           'cuisine',
    label:        'Cuisine',
    icon:         '🌶️',
    stepTitle:    'Cuisine Type',
    stepSubtitle: 'What flavours do you serve?',
    manualStep:   2,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <SelectCousine
        setCousine={(v)   => setForm(f => ({ ...f, cuisine: v }))}
        setAmbience={(v)  => setForm(f => ({ ...f, ambience: v }))}
        setVibe={(v)      => setForm(f => ({ ...f, vibe: v }))}
        setOccasion={(v)  => setForm(f => ({ ...f, occasion: v }))}
      />
    ),

    getValue: (form) => ({
      cuisine:  form.cuisine,
      ambience: form.ambience,
      vibe:     form.vibe,
      occasion: form.occasion,
    }),

    save: async (form, updateRes) => {
      await Promise.all([
        updateRes('cousine',  form.cuisine?.map(c => c.value ?? c)),
        updateRes('ambience', form.ambience?.map(a => a.value ?? a)),
        updateRes('vibe',     form.vibe?.map(v => v.value ?? v)),
        updateRes('occasion', form.occasion?.map(v => v.value ?? v)),
      ])
    },

    validate: () => undefined, // optional

    summaryRow: (form) => [
      'Cuisine',
      form.cuisine?.map(c => c.label || c.value || c).join(', ') || '—',
    ],
  },


  // ── 3: Menu Type ───────────────────────────────────────────────────────────
  {
    id:           'menuType',
    label:        'Menu Type',
    icon:         '🥗',
    stepTitle:    'Menu Type',
    stepSubtitle: 'Veg, Non-veg or both?',
    manualStep:   3,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <SelectMenuType
        setMenuType={(v) => setForm(f => ({ ...f, menuType: v }))}
        menuType={form.menuType}
      />
    ),

    getValue: (form) => form.menuType,

    save: async (form, updateRes) => {
      await updateRes('menuType', form.menuType)
    },

    validate: (form) => {
      if (!form.menuType) return 'Please select a menu type'
    },

    summaryRow: (form) => ['Menu Type', form.menuType || '—'],
  },


// ── 4: Hours ───────────────────────────────────────────────────────────────
{
  id:           'hours',
  label:        'Hours',
  icon:         '🕐',
  stepTitle:    'Opening Hours',
  stepSubtitle: 'When do you open & close?',
  manualStep:   4,
  googleReviewField: true,

  render: ({ form, setForm }) => (
    <div className="ap-hours-row">
      <div>
        <label className="ap-label">
          Opening Time <span className="ap-required">*</span>
        </label>
        <input
          className="ap-input ap-input--time"
          type="time"
          value={form.opensAt}
          onChange={(e) => setForm(f => ({ ...f, opensAt: e.target.value }))}
        />
      </div>
      <div className="ap-hours-sep">→</div>
      <div>
        <label className="ap-label">
          Closing Time <span className="ap-required">*</span>
        </label>
        <input
          className="ap-input ap-input--time"
          type="time"
          value={form.closeAt}
          onChange={(e) => setForm(f => ({ ...f, closeAt: e.target.value }))}
        />
      </div>
      <p className="ap-hours-hint">
        Open round the clock? Set opening to <strong>00:00</strong> and closing to{' '}
        <strong>23:59</strong>. 00:00 → 00:00 isn't allowed since it's ambiguous
        (could mean 0 hours or 24 hours).
      </p>
    </div>
  ),

  getValue: (form) => ({ opensAt: form.opensAt, closeAt: form.closeAt }),

  save: async (form, updateRes) => {
    await Promise.all([
      updateRes('opensAt', timeToMs(form.opensAt)),
      updateRes('CloseAt', timeToMs(form.closeAt)),
    ])
  },

  validate: (form) => {
    if (!form.opensAt) return 'Please select opening time'
    if (!form.closeAt) return 'Please select closing time'

    if (form.opensAt === form.closeAt) {
      return "Opening and closing time can't be the same. For round-the-clock hours, set closing time to 23:59 instead of 00:00."
    }
  },

  summaryRow: (form) => [
    'Hours',
    `${timeStr(form.opensAt)} – ${timeStr(form.closeAt)}`,
  ],
},


  // ── 5: Website ─────────────────────────────────────────────────────────────
  {
    id:           'websiteUrl',
    label:        'Website',
    icon:         '🔗',
    stepTitle:    'Website',
    stepSubtitle: 'Your online home (optional)',
    optional:     true,
    manualStep:   5,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <>
        <label className="ap-label">
          Website URL <span className="ap-optional">(optional)</span>
        </label>
        <input
          className="ap-input"
          type="url"
          value={form.websiteUrl}
          onChange={(e) => setForm(f => ({ ...f, websiteUrl: e.target.value }))}
          placeholder="https://yourrestaurant.com"
        />
      </>
    ),

    getValue: (form) => form.websiteUrl,

    save: async (form, updateRes) => {
      if (form.websiteUrl) await updateRes('website', form.websiteUrl)
    },

    validate: () => undefined,

    summaryRow: (form) => ['Website', form.websiteUrl || '—'],
  },


  // ── 6: Title ───────────────────────────────────────────────────────────────
  {
    id:           'title',
    label:        'Title',
    icon:         '✏️',
    stepTitle:    'Catchy Title',
    stepSubtitle: 'A headline that draws people in',
    optional:     true,
    manualStep:   6,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <>
        <label className="ap-label">Catchy Title</label>
        <input
          className="ap-input"
          value={form.title}
          onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="e.g. New Delhi's Best Biryani"
        />
      </>
    ),

    getValue: (form) => form.title,

    save: async (form, updateRes) => {
      if (form.title) await updateRes('title', form.title)
    },

    validate: () => undefined,

    summaryRow: (form) => ['Title', form.title || '—'],
  },


  // ── 7: Description ─────────────────────────────────────────────────────────
  {
    id:           'description',
    label:        'Description',
    icon:         '📝',
    stepTitle:    'Description',
    stepSubtitle: 'Tell your story',
    optional:     true,
    manualStep:   7,
    googleReviewField: true,

    render: ({ form, setForm }) => (
      <>
        <label className="ap-label">Description</label>
        <textarea
          className="ap-textarea"
          value={form.description}
          onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Tell potential guests what makes you special..."
          rows={5}
        />
      </>
    ),

    getValue: (form) => form.description,

    save: async (form, updateRes) => {
      if (form.description) await updateRes('description', form.description)
    },

    validate: () => undefined,

    // Description handled separately in ReviewSummary
    summaryRow: false,
  },


  // ── 8: Thumbnail ───────────────────────────────────────────────────────────
  {
    id:           'thumbnailImg',
    label:        'Thumbnail',
    icon:         '📸',
    stepTitle:    'Thumbnail',
    stepSubtitle: 'First impressions matter',
    optional:     true,
    manualStep:   8,
    googleReviewField: false, // handled in Google publish step

    render: ({ form, setForm }) => (
      <AddThumbnail
        setThumbnailImg={(v) => setForm(f => ({ ...f, thumbnailImg: v }))}
        thumbnailImg={form.thumbnailImg}
      />
    ),

    getValue: (form) => form.thumbnailImg,

    save: async (form, updateRes) => {
      if (form.thumbnailImg) await updateRes('thumbnail', form.thumbnailImg)
    },

    validate: () => undefined,

    summaryRow: false, // shown as image preview in ReviewSummary
  },


  // ── 9: Gallery ─────────────────────────────────────────────────────────────
  {
    id:           'cafeImg',
    label:        'Gallery',
    icon:         '🖼️',
    stepTitle:    'Gallery Images',
    stepSubtitle: 'Show off your space',
    optional:     true,
    manualStep:   9,
    googleReviewField: false, // handled in Google publish step

    render: ({ form, setForm }) => (
      <AddImages
        setCafeImg={(v) => setForm(f => ({ ...f, cafeImg: v }))}
        cafeImg={form.cafeImg}
      />
    ),

    getValue: (form) => form.cafeImg,

    save: async (form, updateRes) => {
      if (form.cafeImg?.length) await updateRes('images', form.cafeImg)
    },

    validate: () => undefined,

    summaryRow: false,
  },

  // ── 10: Start Date ───────────────────────────────────────────────────────
  {
    id:           'startDate',
    label:        'Start Date',
    icon:         '📅',
    stepTitle:    'Start Date',
    stepSubtitle: 'When did you start (or plan to start) operations?',
    optional:     true,
    manualStep:   10,
    googleReviewField: true,  

     render: ({ form, setForm }) => (
      <AddStartDate
        setStartDate={(v) => setForm(f => ({ ...f, startDate: v }))}
        startDate={form.startDate}
      />
    ),

    getValue: (form) => form.startDate,
    save: async (form, updateRes) => {
      if (form.startDate) await updateRes('startDate', form.startDate)
    },

    validate: () => undefined,

    summaryRow: false,

  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ↓ ADD NEW FIELDS HERE — copy the shape above, assign the next manualStep
  // ═══════════════════════════════════════════════════════════════════════════
]


// ─── Build Step Arrays From Registry ──────────────────────────────────────────
//
// Manual flow: one step per registry entry, sorted by manualStep, with a
// final "Review" step auto-appended.
//
// Google flow: fixed 3-step structure (search → review → publish).

const MANUAL_FIELD_STEPS = [...FIELD_REGISTRY]
  .sort((a, b) => a.manualStep - b.manualStep)

const MANUAL_STEPS = [
  ...MANUAL_FIELD_STEPS.map((field, i) => ({
    id:       i,
    label:    field.label,
    icon:     field.icon,
    title:    field.stepTitle,
    subtitle: field.stepSubtitle,
    fieldId:  field.id,
  })),
  {
    id:       MANUAL_FIELD_STEPS.length,
    label:    'Review',
    icon:     '✅',
    title:    'Review & Publish',
    subtitle: 'Everything look good?',
    fieldId:  '__review__',
  },
]

const GOOGLE_STEPS = [
  { id: 0, label: 'Search',  icon: '🔍', title: 'Find Your Restaurant', subtitle: 'Search Google Places to auto-fill details', fieldId: '__google_search__' },
  { id: 1, label: 'Review',  icon: '✏️', title: 'Review & Edit',        subtitle: 'Confirm or adjust the fetched details',      fieldId: '__google_review__' },
  { id: 2, label: 'Publish', icon: '🚀', title: 'Finish & Publish',     subtitle: 'Add images and go live',                     fieldId: '__google_publish__' },
]

// Registry lookup by fieldId
const fieldById = Object.fromEntries(FIELD_REGISTRY.map(f => [f.id, f]))

// Fields shown in Google review form
const GOOGLE_REVIEW_FIELDS = FIELD_REGISTRY.filter(f => f.googleReviewField)


// ─── Sub-Components ────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null
  const icons = { success: '✓', error: '✕', info: 'ℹ' }
  return (
    <div className={`ap-toast ap-toast--${toast.type}`}>
      <span className="ap-toast-icon">{icons[toast.type]}</span>
      {toast.msg}
    </div>
  )
}

function ProgressBar({ value }) {
  return (
    <div className="ap-progress-wrap">
      <div className="ap-progress-bar">
        <div className="ap-progress-fill" style={{ width: `${value}%` }} />
      </div>
      <span className="ap-progress-pct">{value}%</span>
    </div>
  )
}

function StepIcon({ step, isCurrent, isDone }) {
  return (
    <div className={`ap-step-dot ${isCurrent ? 'current' : ''} ${isDone ? 'done' : ''}`}>
      {isDone ? <span className="ap-step-check">✓</span> : <span>{step.icon}</span>}
    </div>
  )
}

function Sidebar({ steps, curIndex }) {
  const progress = Math.round((curIndex / Math.max(steps.length - 1, 1)) * 100)
  return (
    <aside className="ap-sidebar">
      <div className="ap-brand">
        <span className="ap-brand-icon">🍴</span>
        <div>
          <div className="ap-brand-name">Restro Pilots</div>
          <div className="ap-brand-sub">Partner Onboarding</div>
        </div>
      </div>
      <Link to="/partner" className="ap-back-link">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Dashboard
      </Link>
      <nav className="ap-steps-nav">
        {steps.map((s, i) => (
          <div
            key={s.id}
            className={`ap-step-row ${i === curIndex ? 'active' : ''} ${i < curIndex ? 'done' : ''}`}
          >
            <StepIcon step={s} isCurrent={i === curIndex} isDone={i < curIndex} />
            <div className="ap-step-info">
              <div className="ap-step-label">{s.label}</div>
              {i === curIndex && <div className="ap-step-hint">{s.subtitle}</div>}
            </div>
          </div>
        ))}
      </nav>
      <div className="ap-sidebar-footer">
        <ProgressBar value={progress} />
      </div>
    </aside>
  )
}

function ModeSelector({ onSelect }) {
  return (
    <div className="ap-mode-root">
      <aside className="ap-mode-sidebar">
        <div className="ap-brand">
          <span className="ap-brand-icon">🍴</span>
          <div>
            <div className="ap-brand-name">Restaurant Hub</div>
            <div className="ap-brand-sub">Partner Onboarding</div>
          </div>
        </div>
        <Link to="/partner" className="ap-back-link">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Dashboard
        </Link>
        <div className="ap-mode-sidebar-art">
          <div className="ap-art-circle ap-art-c1" />
          <div className="ap-art-circle ap-art-c2" />
          <div className="ap-art-circle ap-art-c3" />
          <span className="ap-art-emoji">🏪</span>
        </div>
      </aside>
      <main className="ap-mode-main">
        <div className="ap-mode-inner">
          <div className="ap-mode-header">
            <h1 className="ap-mode-title">Add Your Property</h1>
            <p className="ap-mode-subtitle">
              Choose how you'd like to set up your restaurant profile.
              You can always edit details later.
            </p>
          </div>
          <div className="ap-mode-cards">
            <button className="ap-mode-card ap-mode-card--featured" onClick={() => onSelect('google')}>
              <div className="ap-mode-card-badge">Recommended</div>
              <div className="ap-mode-card-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="#fff" fillOpacity="0.12"/>
                  <path d="M22.5 16.18c0-.56-.05-1.1-.14-1.62H16v3.07h3.65a3.12 3.12 0 01-1.35 2.04v1.7h2.19c1.28-1.18 2.01-2.92 2.01-5.19z" fill="#4285F4"/>
                  <path d="M16 23c1.84 0 3.38-.61 4.5-1.65l-2.19-1.7c-.61.41-1.38.65-2.31.65-1.78 0-3.29-1.2-3.83-2.82H9.91v1.75A6.98 6.98 0 0016 23z" fill="#34A853"/>
                  <path d="M12.17 17.48A4.2 4.2 0 0111.95 16c0-.51.09-1.01.22-1.48V12.77H9.91A7.01 7.01 0 009 16c0 1.13.27 2.2.91 3.23l2.26-1.75z" fill="#FBBC05"/>
                  <path d="M16 11.7c1 0 1.9.34 2.6 1.02l1.96-1.96C19.38 9.68 17.84 9 16 9a6.98 6.98 0 00-6.09 3.77l2.26 1.75C12.71 12.9 14.22 11.7 16 11.7z" fill="#EA4335"/>
                </svg>
              </div>
              <h2 className="ap-mode-card-title">Fetch from Google</h2>
              <p className="ap-mode-card-desc">
                Search for your restaurant on Google Places and we'll auto-fill your name, location, hours, website, and more in seconds.
              </p>
              <ul className="ap-mode-card-perks">
                <li><span className="perk-check">✓</span> Auto-fills most fields</li>
                <li><span className="perk-check">✓</span> Ready in ~3 steps</li>
                <li><span className="perk-check">✓</span> Review before publishing</li>
              </ul>
              <span className="ap-mode-card-cta">Get Started →</span>
            </button>
            <button className="ap-mode-card" onClick={() => onSelect('manual')}>
              <div className="ap-mode-card-icon ap-mode-card-icon--muted">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="#fff" fillOpacity="0.06"/>
                  <path d="M10 22l2-6 8-8 4 4-8 8-6 2zm2-6l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="ap-mode-card-title">Fill Manually</h2>
              <p className="ap-mode-card-desc">
                Enter all your restaurant details step by step. Full control over every field.
              </p>
              <ul className="ap-mode-card-perks">
                <li><span className="perk-check perk-check--dim">·</span> Full control over every field</li>
                <li><span className="perk-check perk-check--dim">·</span> ~{MANUAL_STEPS.length} steps to complete</li>
                <li><span className="perk-check perk-check--dim">·</span> Great for new listings</li>
              </ul>
              <span className="ap-mode-card-cta ap-mode-card-cta--ghost">Start Manually →</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}

function GoogleSearchStep({ onPlaceSelected }) {
  const [query,    setQuery]    = useState('')
  const [results,  setResults]  = useState([])
  const [loading,  setLoading]  = useState(false)
  const [fetching, setFetching] = useState(false)
  const debounceRef = useRef(null)

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return }
    setLoading(true)
    try { setResults(await searchPlaces(q)) }
    catch { setResults([]) }
    setLoading(false)
  }, [])

  const handleChange = (e) => {
    const v = e.target.value
    setQuery(v)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(v), 450)
  }

  const handleSelect = async (place) => {
    setFetching(true)
    try {
      const details = await fetchPlaceDetails(place.place_id)
      onPlaceSelected(details, place)
    } catch { onPlaceSelected(null, place) }
    setFetching(false)
  }

  return (
    <div className="ap-google-search">
      <div className="ap-search-box">
        <svg className="ap-search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="1.7"/>
          <path d="M12 12l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
        </svg>
        <input
          className="ap-search-input"
          value={query}
          onChange={handleChange}
          placeholder="Search your restaurant name or address…"
          autoFocus
        />
        {loading && <div className="ap-search-spinner" />}
      </div>
      {results.length > 0 && (
        <div className="ap-search-results">
          {results.map((r) => (
            <button
              key={r.place_id}
              className="ap-search-result"
              onClick={() => handleSelect(r)}
              disabled={fetching}
            >
              <div className="ap-result-icon">📍</div>
              <div className="ap-result-info">
                <div className="ap-result-name">{r.name}</div>
                <div className="ap-result-addr">{r.formatted_address}</div>
              </div>
              {fetching && <div className="ap-search-spinner" />}
            </button>
          ))}
        </div>
      )}
      {query.length > 2 && !loading && results.length === 0 && (
        <div className="ap-search-empty">
          <span>No results found.</span>
          <span className="ap-search-empty-hint"> Try a different name or switch to manual entry.</span>
        </div>
      )}
      <div className="ap-google-note">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4"/>
          <path d="M7 6v4M7 4.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
        Results powered by Google Places. Data is fetched for review — nothing is saved until you confirm.
      </div>
    </div>
  )
}

/**
 * Google review form — rendered from GOOGLE_REVIEW_FIELDS automatically.
 * Any field with googleReviewField: true appears here without extra code.
 */
function GoogleReviewForm({ form, setForm }) {
  return (
    <div className="ap-review-form">
      <div className="ap-review-grid">
        {GOOGLE_REVIEW_FIELDS.map(field => (
          <div
            key={field.id}
            className={`ap-rf-field ${
              ['nameLocation', 'cuisine', 'category', 'description'].includes(field.id)
                ? 'ap-rf-field--full'
                : ''
            }`}
          >
            <field.render form={form} setForm={setForm} />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Final review summary — driven by summaryRow() in each field definition.
 * Any field that defines summaryRow: (form) => [key, value] appears here.
 */
function ReviewSummary({ form }) {
  const rows = FIELD_REGISTRY
    .filter(f => typeof f.summaryRow === 'function')
    .map(f => f.summaryRow(form))
    .filter(Boolean)

  return (
    <div className="ap-review-summary">
      <div className="ap-rs-grid">
        {rows.map(([k, v]) => (
          <div key={k} className="ap-rs-row">
            <span className="ap-rs-key">{k}</span>
            <span className="ap-rs-val">{v}</span>
          </div>
        ))}
      </div>
      {form.description && (
        <div className="ap-rs-desc">
          <span className="ap-rs-key">Description</span>
          <p className="ap-rs-val ap-rs-val--desc">{form.description}</p>
        </div>
      )}
      <div className="ap-rs-media">
        {form.thumbnailImg && (
          <div className="ap-rs-thumb">
            <img src={form.thumbnailImg} alt="Thumbnail" />
            <span>Thumbnail</span>
          </div>
        )}
        {form.cafeImg?.length > 0 && (
          <div className="ap-rs-gallery-count">
            🖼️ {form.cafeImg.length} gallery image{form.cafeImg.length > 1 ? 's' : ''} added
          </div>
        )}
      </div>
    </div>
  )
}


// ─── Main Component ────────────────────────────────────────────────────────────

const AddProperty = () => {
  const [mode,     setMode]     = useState(null)
  const [curIndex, setCurIndex] = useState(0)
  const [form,     setForm]     = useState({ ...EMPTY_FORM })
  const [resId,    setResId]    = useState(null)
  const [saving,   setSaving]   = useState(false)
  const [toast,    setToast]    = useState(null)
  const navigate = useNavigate();
  const steps = mode === 'google' ? GOOGLE_STEPS : MANUAL_STEPS

  // ── Auth ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const verify = async () => {
      try {
        const res = await fetch(`${host}/user/partnerAuth`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: token() },
        })
        if (!res.ok) window.location.href = '/partner'
      } catch { window.location.href = '/partner' }
    }
    verify()
  }, [])

  // ── Toast ───────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3200)
  }, [])

  // ── API helpers ─────────────────────────────────────────────────────────────
  const addNameLocation = async () => {
    const res = await fetch(`${host}/partner/addProperty`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token() },
      body: JSON.stringify({ name: form.resname, location: form.location }),
    })
    if (!res.ok) throw new Error('Failed to create property')
    const data = await res.json()
    setResId(data._id)
    return data._id
  }

  const updateRes = async (parameter, value, id = resId) => {
    if (value === null || value === undefined || value === '') return
    await fetch(`${host}/cafe/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token() },
      body: JSON.stringify({ parameter, value, resId: id }),
    })
  }

  /**
   * Save every field in the registry via its save() fn.
   * Used by Google path (saves all at once after review).
   */
  const saveAllFields = async (id) => {
    const updateResWithId = (param, val) => updateRes(param, val, id)
    await Promise.all(
      FIELD_REGISTRY
        .filter(f => f.id !== 'nameLocation') // handled by addNameLocation
        .map(f => f.save(form, updateResWithId))
    )
  }

  // ── Google path: place selected ─────────────────────────────────────────────
  const handlePlaceSelected = (details, rawPlace) => {
    setForm(f => ({
      ...f,
      ...mapPlaceToForm(details, rawPlace),
    }))
    setCurIndex(1)
  }

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    if (mode === 'manual') {
      const step = MANUAL_STEPS[curIndex]
      if (step.fieldId === '__review__') return true
      const field = fieldById[step.fieldId]
      if (field?.validate) {
        const err = field.validate(form)
        if (err) { showToast(err, 'error'); return false }
      }
    }
    if (mode === 'google' && curIndex === 1) {
      if (!form.resname.trim()) {
        showToast('Please confirm the restaurant name', 'error')
        return false
      }
    }
    return true
  }

  // ── Navigation + save ───────────────────────────────────────────────────────
  const handleNext = async () => {
    if (!validate()) return
    setSaving(true)

    try {
      // ── MANUAL FLOW ──────────────────────────────────────────────────────
      if (mode === 'manual') {
        const step   = MANUAL_STEPS[curIndex]
        const isLast = curIndex === MANUAL_STEPS.length - 1

        if (step.fieldId === 'nameLocation') {
          // Special: creates the property record
          await addNameLocation()
          showToast('Property created!', 'success')
          setCurIndex(c => c + 1)
          setSaving(false)
          return
        }

       if (isLast) {
  // Review step — save any outstanding fields not yet persisted
  await saveAllFields(resId);
  showToast('Published! 🎉', 'success');
  setSaving(false);

  // Wait 500ms
  await new Promise((resolve) => setTimeout(resolve, 500));

  navigate('/partner'); // React Router v6
}

        // Normal field step — call the field's own save fn
        const field = fieldById[step.fieldId]
        if (field?.save) await field.save(form, updateRes)

        showToast('Saved!', 'success')
        setCurIndex(c => c + 1)
      }

      // ── GOOGLE FLOW ──────────────────────────────────────────────────────
      if (mode === 'google') {
        if (curIndex === 1) {
          const id = await addNameLocation()
          await saveAllFields(id)
          showToast('Details saved!', 'success')
          setCurIndex(2)
          setSaving(false)
          return
        }

        if (curIndex === 2) {
          await Promise.all([
            form.thumbnailImg && updateRes('thumbnail', form.thumbnailImg),
            form.cafeImg?.length && updateRes('images', form.cafeImg),
          ].filter(Boolean))
          showToast('Published! 🎉', 'success')
            await new Promise((resolve) => setTimeout(resolve, 500));

  navigate('/partner'); // React Router v6
        }
      }
    } catch (err) {
      console.error(err)
      showToast('Something went wrong. Please retry.', 'error')
    }

    setSaving(false)
  }

  const handleBack = () => {
    if (curIndex === 0) { setMode(null); return }
    setCurIndex(c => c - 1)
  }

  const resetToModeSelect = () => {
    setMode(null)
    setCurIndex(0)
    setForm({ ...EMPTY_FORM })
    setResId(null)
  }

  // ── Render step content ─────────────────────────────────────────────────────
  const renderContent = () => {
    if (mode === 'google') {
      switch (GOOGLE_STEPS[curIndex].fieldId) {
        case '__google_search__':
          return <GoogleSearchStep onPlaceSelected={handlePlaceSelected} />

        case '__google_review__':
          return <GoogleReviewForm form={form} setForm={setForm} />

        case '__google_publish__':
          return (
            <div className="ap-images-step">
              <p className="ap-images-note">
                Your details are saved. Optionally add images to complete your profile.
              </p>
              <div className="ap-images-grid">
                <div>
                  <label className="ap-label">Thumbnail Image</label>
                  <AddThumbnail
                    setThumbnailImg={(v) => setForm(f => ({ ...f, thumbnailImg: v }))}
                    thumbnailImg={form.thumbnailImg}
                  />
                </div>
                <div>
                  <label className="ap-label">Gallery Images</label>
                  <AddImages
                    setCafeImg={(v) => setForm(f => ({ ...f, cafeImg: v }))}
                    cafeImg={form.cafeImg}
                  />
                </div>
              </div>
            </div>
          )

        default: return null
      }
    }

    // Manual flow — look up field from registry by the current step's fieldId
    const step = MANUAL_STEPS[curIndex]

    if (step.fieldId === '__review__') {
      return <ReviewSummary form={form} />
    }

    const field = fieldById[step.fieldId]
    if (!field) return null

    return <field.render form={form} setForm={setForm} resId={resId} />
  }

  // ── Mode selector ───────────────────────────────────────────────────────────
  if (!mode) return <ModeSelector onSelect={setMode} />

  const step     = steps[curIndex]
  const isLastStep = curIndex === steps.length - 1
  const progress = Math.round((curIndex / Math.max(steps.length - 1, 1)) * 100)

  return (
    <div className="ap-root">
      <Sidebar steps={steps} curIndex={curIndex} />

      <main className="ap-main">
        <div className="ap-main-inner">
          {/* Step header */}
          <div className="ap-step-header">
            <div className="ap-step-badge">{step.icon}</div>
            <div>
              <p className="ap-step-sub">
                {mode === 'google' ? 'Quick Setup' : 'Manual Setup'} · Step {curIndex + 1} of {steps.length}
              </p>
              <h1 className="ap-step-title">{step.title}</h1>
              <p className="ap-step-subtitle">{step.subtitle}</p>
            </div>
            <button className="ap-switch-mode" onClick={resetToModeSelect}>
              Switch method
            </button>
          </div>

          {/* Mobile progress */}
          <div className="ap-mobile-progress">
            <ProgressBar value={progress} />
          </div>

          {/* Card */}
          <div className="ap-card" key={`${mode}-${curIndex}`}>
            {renderContent()}
          </div>

          {/* Actions */}
          <div className="ap-actions">
            <button className="ap-btn ap-btn--ghost" onClick={handleBack}>
              ← {curIndex === 0 ? 'Change Method' : 'Back'}
            </button>

            {/* Google step 0: no Next button — selecting a result advances */}
            {!(mode === 'google' && curIndex === 0) && (
              <button
                className="ap-btn ap-btn--primary"
                onClick={handleNext}
                disabled={saving}
              >
                {saving && <span className="ap-spinner" />}
                {saving
                  ? 'Saving…'
                  : isLastStep
                    ? 'Finish & Publish 🎉'
                    : 'Save & Continue →'}
              </button>
            )}
          </div>
        </div>
      </main>

      <Toast toast={toast} />
    </div>
  )
}

export default AddProperty